import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import {
  FileSignature, FileText, Download, Upload, Send, PenLine, CheckCircle2,
  Clock, Lock, AlertTriangle, X, Plus, User as UserIcon, Filter, RefreshCw, Trash2, Sparkles, Inbox,
  Undo2, ExternalLink, LayoutGrid, List as ListIcon
} from "lucide-react";
import { CATEGORY_LABEL, STATUS_LABEL } from "./constants";

export function DocsBoard({ docs, templates, onOpen, onChanged }) {
  const navigate = useNavigate();
  const tplByKey = useMemo(() => Object.fromEntries(templates.map((t) => [t.key, t])), [templates]);
  const [cat, setCat] = useState("all");
  const [type, setType] = useState("all");
  const [statusF, setStatusF] = useState("all");
  const [layout, setLayout] = useState("grouped"); // grouped | recent
  const [expanded, setExpanded] = useState(new Set());
  const toggleGroup = (id) => setExpanded((prev) => {
    const n = new Set(prev);
    n.has(id) ? n.delete(id) : n.add(id);
    return n;
  });

  const STATUS_GROUPS = {
    awaiting: { label: "AWAITING SIGN", match: (s) => s === "pending_employee" || s === "pending_hr" },
    completed: { label: "COMPLETED", match: (s) => s === "completed" },
    draft: { label: "DRAFT / PENDING", match: (s) => s === "draft" },
  };

  // Category → set of template keys (only categories that actually have docs)
  const catList = useMemo(() => {
    const cats = new Set();
    docs.forEach((d) => cats.add(tplByKey[d.template_type]?.category || "other"));
    const order = ["onboarding", "in_service", "compliance", "exit", "other"];
    return order.filter((c) => cats.has(c));
  }, [docs, tplByKey]);

  // Type pills scoped to the selected category
  const typeList = useMemo(() => {
    const counts = {};
    docs.forEach((d) => {
      const c = tplByKey[d.template_type]?.category || "other";
      if (cat !== "all" && c !== cat) return;
      counts[d.template_type] = (counts[d.template_type] || 0) + 1;
    });
    return Object.entries(counts).sort((a, b) => b[1] - a[1]);
  }, [docs, tplByKey, cat]);

  const filtered = useMemo(() => {
    return docs.filter((d) => {
      const c = tplByKey[d.template_type]?.category || "other";
      if (cat !== "all" && c !== cat) return false;
      if (type !== "all" && d.template_type !== type) return false;
      if (statusF !== "all" && !STATUS_GROUPS[statusF]?.match(d.status)) return false;
      return true;
    });
  }, [docs, tplByKey, cat, type, statusF]); // eslint-disable-line react-hooks/exhaustive-deps

  // Group by employee for the "grouped" layout
  const groups = useMemo(() => {
    const m = {};
    filtered.forEach((d) => {
      const key = d.employee_id || d.employee_name_at_create || "unknown";
      (m[key] ||= { name: d.employee_name_at_create || d.employee_name || "—", code: d.employee_code || "", docs: [] }).docs.push(d);
    });
    return Object.entries(m)
      .map(([id, g]) => ({ id, ...g, latest: Math.max(...g.docs.map((x) => new Date(x.updated_at).getTime())) }))
      .sort((a, b) => b.latest - a.latest);
  }, [filtered]);

  const recentSorted = useMemo(
    () => [...filtered].sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at)),
    [filtered]
  );

  const catCount = (c) => docs.filter((d) => (tplByKey[d.template_type]?.category || "other") === c).length;

  const onRecall = async (e, d) => {
    e.stopPropagation();
    if (!confirm(`Recall "${tplByKey[d.template_type]?.title || d.template_type}" for ${d.employee_name_at_create || "employee"} back to DRAFT? Any signatures already collected will be cleared so you can correct & re-issue.`)) return;
    try {
      await api.post(`/hr/documents/${d.id}/recall`);
      toast.success("Recalled to draft — you can now edit & resend");
      onChanged && onChanged();
    } catch (err) { toast.error(err.response?.data?.detail || "Recall failed"); }
  };
  const onDelete = async (e, d) => {
    e.stopPropagation();
    const signed = d.status === "completed";
    if (!confirm(`Delete "${tplByKey[d.template_type]?.title || d.template_type}" for ${d.employee_name_at_create || "employee"}?${signed ? " This document is SIGNED and will be permanently removed from the audit trail." : ""} This cannot be undone.`)) return;
    try {
      await api.delete(`/hr/documents/${d.id}`);
      toast.success("Document deleted");
      onChanged && onChanged();
    } catch (err) { toast.error(err.response?.data?.detail || "Delete failed"); }
  };
  const onPdf = async (e, d) => {
    e.stopPropagation();
    const tid = toast.loading("Preparing PDF…");
    try {
      const res = await api.get(`/hr/documents/${d.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `${(tplByKey[d.template_type]?.title || d.template_type).replace(/\s+/g, "_")}_${d.id}.pdf`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 60000);
      toast.success("PDF downloaded", { id: tid });
    } catch (err) { toast.error(err.response?.data?.detail || "PDF download failed", { id: tid }); }
  };

  const Pill = ({ active, onClick, children, testid }) => (
    <button onClick={onClick} data-testid={testid}
      className={`px-3 py-1 rounded-full text-[11px] font-mono font-bold border-2 transition-colors ${active ? "bg-zinc-900 text-white border-zinc-900" : "bg-white text-zinc-700 border-zinc-300 hover:border-zinc-900"}`}>
      {children}
    </button>
  );

  const DocBox = ({ d }) => {
    const st = STATUS_LABEL[d.status] || { label: d.status, cls: "bg-zinc-100" };
    const t = tplByKey[d.template_type];
    return (
      <div onClick={() => onOpen(d)} data-testid={`doc-box-${d.id}`}
        className="brut-card bg-white p-3 cursor-pointer hover:-translate-y-0.5 hover:shadow-md transition-all flex flex-col gap-2">
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0">
            <FileText className="w-4 h-4 text-zinc-500 shrink-0" />
            <span className="font-bold text-xs truncate">{t?.title || d.template_type}</span>
          </div>
          <span className={`shrink-0 px-1.5 py-0.5 rounded-full text-[8px] font-bold ${st.cls}`}>{st.label}</span>
        </div>
        <div className="text-[10px] font-mono text-zinc-500 truncate">{d.employee_name_at_create || d.employee_id}</div>
        <div className="text-[9px] font-mono text-zinc-400">{new Date(d.updated_at).toLocaleString()}</div>
        <div className="flex items-center gap-2 pt-1 border-t border-zinc-100">
          <button onClick={(e) => onPdf(e, d)} className="text-zinc-600 hover:text-zinc-900" title="Download PDF" data-testid={`doc-pdf-${d.id}`}>
            <Download className="w-3.5 h-3.5" />
          </button>
          {d.status !== "draft" && (
            <button onClick={(e) => onRecall(e, d)} className="text-amber-600 hover:text-amber-800" title="Recall to draft & edit" data-testid={`doc-recall-${d.id}`}>
              <Undo2 className="w-3.5 h-3.5" />
            </button>
          )}
          <button onClick={(e) => onDelete(e, d)} className="text-rose-600 hover:text-rose-800" title="Delete document" data-testid={`doc-delete-${d.id}`}>
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-3" data-testid="docs-board">
      {/* Category pills */}
      <div className="flex flex-wrap items-center gap-1.5">
        <span className="text-[10px] font-mono text-zinc-400 mr-1">CATEGORY</span>
        <Pill active={cat === "all"} onClick={() => { setCat("all"); setType("all"); }} testid="cat-pill-all">All <span className="opacity-60">{docs.length}</span></Pill>
        {catList.map((c) => (
          <Pill key={c} active={cat === c} onClick={() => { setCat(c); setType("all"); }} testid={`cat-pill-${c}`}>
            {CATEGORY_LABEL[c] || c} <span className="opacity-60">{catCount(c)}</span>
          </Pill>
        ))}
        <div className="ml-auto flex items-center gap-1">
          <button onClick={() => setLayout("grouped")} className={`p-1.5 rounded border-2 ${layout === "grouped" ? "border-zinc-900 bg-zinc-900 text-white" : "border-zinc-300 text-zinc-600"}`} title="Group by employee" data-testid="layout-grouped"><LayoutGrid className="w-3.5 h-3.5" /></button>
          <button onClick={() => setLayout("recent")} className={`p-1.5 rounded border-2 ${layout === "recent" ? "border-zinc-900 bg-zinc-900 text-white" : "border-zinc-300 text-zinc-600"}`} title="Sort by most recent" data-testid="layout-recent"><ListIcon className="w-3.5 h-3.5" /></button>
        </div>
      </div>
      {/* Status pills */}
      <div className="flex flex-wrap items-center gap-1.5">
        <span className="text-[10px] font-mono text-zinc-400 mr-1">STATUS</span>
        <Pill active={statusF === "all"} onClick={() => setStatusF("all")} testid="status-pill-all">All <span className="opacity-60">{docs.length}</span></Pill>
        {Object.entries(STATUS_GROUPS).map(([k, g]) => {
          const n = docs.filter((d) => g.match(d.status)).length;
          return (
            <Pill key={k} active={statusF === k} onClick={() => setStatusF(k)} testid={`status-pill-${k}`}>
              {g.label} <span className="opacity-60">{n}</span>
            </Pill>
          );
        })}
      </div>
      {/* Type pills */}
      <div className="flex flex-wrap items-center gap-1.5">
        <span className="text-[10px] font-mono text-zinc-400 mr-1">TYPE</span>
        <Pill active={type === "all"} onClick={() => setType("all")} testid="type-pill-all">All Types</Pill>
        {typeList.map(([k, n]) => (
          <Pill key={k} active={type === k} onClick={() => setType(k)} testid={`type-pill-${k}`}>
            {tplByKey[k]?.title || k} <span className="opacity-60">{n}</span>
          </Pill>
        ))}
      </div>

      {filtered.length === 0 && (
        <div className="brut-card p-6 text-xs font-mono text-zinc-500 text-center">No documents match this filter.</div>
      )}

      {layout === "recent" && filtered.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2" data-testid="docs-recent-grid">
          {recentSorted.map((d) => <DocBox key={d.id} d={d} />)}
        </div>
      )}

      {layout === "grouped" && groups.length > 1 && (
        <div className="flex justify-end gap-2">
          <button onClick={() => setExpanded(new Set(groups.map((g) => g.id)))} className="brut-btn-secondary px-2.5 py-1 text-[10px] font-mono" data-testid="expand-all-groups">▼ EXPAND ALL</button>
          <button onClick={() => setExpanded(new Set())} className="brut-btn-secondary px-2.5 py-1 text-[10px] font-mono" data-testid="collapse-all-groups">▲ COLLAPSE ALL</button>
        </div>
      )}
      {layout === "grouped" && groups.map((g) => (
        <div key={g.id} className="brut-card bg-zinc-50 px-3 py-2" data-testid={`emp-group-${g.id}`}>
          <div className="flex items-center justify-between flex-wrap gap-1">
            <div className="flex items-center gap-2 min-w-0">
              <button onClick={() => toggleGroup(g.id)} className="brut-btn-secondary px-1.5 py-0.5 text-[10px] font-mono shrink-0" title={expanded.has(g.id) ? "Collapse" : "Expand"} data-testid={`toggle-group-${g.id}`}>
                {expanded.has(g.id) ? "▲" : "▼"}
              </button>
              <button
                onClick={() => navigate(`/users?q=${encodeURIComponent(g.code || g.name)}`)}
                className="flex items-center gap-1.5 text-xs font-black hover:underline min-w-0"
                title="Open employee profile" data-testid={`emp-link-${g.id}`}>
                <UserIcon className="w-3.5 h-3.5 shrink-0" /> <span className="truncate">{g.name}</span>
                {g.code && <span className="text-[10px] font-mono text-zinc-500">· {g.code}</span>}
                <ExternalLink className="w-3 h-3 text-zinc-400 shrink-0" />
              </button>
              {!expanded.has(g.id) && (
                <span className="flex gap-1 flex-wrap">
                  {Object.entries(g.docs.reduce((m, d) => ((m[d.status] = (m[d.status] || 0) + 1), m), {})).map(([s, n]) => {
                    const st = STATUS_LABEL[s] || { label: s, cls: "bg-zinc-100" };
                    return <span key={s} className={`px-1.5 py-0.5 rounded-full text-[8px] font-bold ${st.cls}`}>{n} {st.label}</span>;
                  })}
                </span>
              )}
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={async (e) => {
                  e.stopPropagation();
                  const tid = toast.loading(`Building ZIP for ${g.name}…`);
                  try {
                    const res = await api.get(`/hr/documents/export/zip?employee_id=${encodeURIComponent(g.id)}`, { responseType: "blob", timeout: 300000 });
                    const url = URL.createObjectURL(new Blob([res.data], { type: "application/zip" }));
                    const a = document.createElement("a");
                    a.href = url;
                    a.download = `${(g.name || "Employee").replace(/[^A-Za-z0-9]/g, "")}_${(g.code || "NA").replace(/[^A-Za-z0-9-]/g, "")}_Documents.zip`;
                    a.click();
                    setTimeout(() => URL.revokeObjectURL(url), 60000);
                    toast.success(`${g.name}'s documents downloaded`, { id: tid });
                  } catch (e2) { toast.error(e2.response?.data?.detail || "Export failed", { id: tid }); }
                }}
                className="brut-btn-secondary px-2 py-1 text-[10px] font-mono inline-flex items-center gap-1 bg-sky-100 hover:bg-sky-200"
                title={`Download all of ${g.name}'s documents as one ZIP folder`}
                data-testid={`emp-download-zip-${g.id}`}
              >
                <Download className="w-3 h-3" /> FOLDER
              </button>
              <span className="text-[10px] font-mono text-zinc-400">{g.docs.length} doc{g.docs.length > 1 ? "s" : ""}</span>
            </div>
          </div>
          {expanded.has(g.id) && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 mt-2">
              {g.docs.sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at)).map((d) => <DocBox key={d.id} d={d} />)}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

export function DocsTable({ docs, templates, onOpen, isMineView, isAdmin, onChanged }) {
  const tplByKey = Object.fromEntries(templates.map((t) => [t.key, t]));
  const onDelete = async (e, d) => {
    e.stopPropagation();
    if (!confirm(`Delete this DRAFT ${tplByKey[d.template_type]?.title || d.template_type}? Signed documents can never be deleted; only drafts can.`)) return;
    try {
      await api.delete(`/hr/documents/${d.id}`);
      toast.success("Draft deleted");
      onChanged && onChanged();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Delete failed");
    }
  };
  if (!docs.length) {
    return <div className="brut-card p-6 text-xs font-mono text-zinc-500 text-center">No documents yet.</div>;
  }
  return (
    <div className="brut-card bg-white overflow-hidden">
      <table className="w-full text-xs font-mono" data-testid="docs-table">
        <thead className="bg-zinc-50 border-b border-zinc-300">
          <tr>
            <th className="text-left p-2">TEMPLATE</th>
            {!isMineView && <th className="text-left p-2">EMPLOYEE</th>}
            <th className="text-center p-2">STATUS</th>
            <th className="text-left p-2">UPDATED</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {docs.map((d) => {
            const st = STATUS_LABEL[d.status] || { label: d.status, cls: "bg-zinc-100" };
            const t = tplByKey[d.template_type];
            const isDraft = d.status === "draft";
            return (
              <tr key={d.id} className="border-b border-zinc-100 hover:bg-zinc-50 cursor-pointer" onClick={() => onOpen(d)} data-testid={`doc-row-${d.id}`}>
                <td className="p-2 font-bold">{t?.title || d.template_type}</td>
                {!isMineView && <td className="p-2">{d.employee_name_at_create || d.employee_id}</td>}
                <td className="p-2 text-center">
                  <span className={`inline-block px-1.5 py-0.5 rounded-full text-[9px] font-bold ${st.cls}`}>{st.label}</span>
                </td>
                <td className="p-2 text-zinc-500">{new Date(d.updated_at).toLocaleString()}</td>
                <td className="p-2 text-right whitespace-nowrap">
                  {(isAdmin || (d.signed_at_employee && d.signed_at_hr)) ? (
                  <button onClick={async (e) => {
                    e.stopPropagation();
                    // SEC-004 fix: authenticated blob download instead of `?auth=<token>` in URL
                    const tid = toast.loading("Preparing PDF…");
                    try {
                      const res = await api.get(`/hr/documents/${d.id}/pdf`, { responseType: "blob" });
                      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
                      const a = document.createElement("a");
                      a.href = url;
                      a.download = `${(t?.title || d.template_type).replace(/\s+/g, "_")}_${d.id}.pdf`;
                      a.click();
                      setTimeout(() => URL.revokeObjectURL(url), 60000);
                      toast.success("PDF downloaded", { id: tid });
                    } catch (err) { toast.error(err.response?.data?.detail || "PDF download failed", { id: tid }); }
                  }}
                    className="text-zinc-600 hover:text-zinc-900 mr-2" title="Download PDF"
                    data-testid={`doc-pdf-${d.id}`}>
                    <Download className="w-3.5 h-3.5" />
                  </button>
                  ) : (
                  <button onClick={(e) => { e.stopPropagation(); onOpen(d); }}
                    className="text-zinc-400 mr-2" title="Strictly private — download unlocks after BOTH signatures. Open to view."
                    data-testid={`doc-locked-${d.id}`}>
                    <Lock className="w-3.5 h-3.5" />
                  </button>
                  )}
                  {isDraft && (
                    <button onClick={(e) => onDelete(e, d)}
                      className="text-rose-600 hover:text-rose-800" title="Delete draft"
                      data-testid={`doc-delete-${d.id}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

